import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import classes from "./AppDownload.module.scss";
import LazyImage from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import getImagePath from "@Utils/getImagePath";
import config from "config/index";
import {
  APP_DOWNLOAD_APP_STORE_LINK,
  APP_DOWNLOAD_PLAY_STORE_LINK,
  CHINA_ANDROID_DOWNLOAD_LINK,
  ICP_LICENCE_NUMBER,
  ICP_URL,
} from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsApp from "hooks/useIsApp";
import AndroidStoreIcon from "@Icons/AndroidStoreIcon";
import AndroidStoreBadgeChina from "@Icons/AndroidStoreBadgeChina";
import AppStoreBadgeChina from "@Icons/AppStoreBadgeChina";

import ChinaPaymentLogos from "@Components/ChinaPaymentLogos";
import FooterQRRowChina from "../FooterQRRowChina/FooterQRRowChina";

const AppDownload = ({ isDesktop = false }) => {
  const { t } = useTranslationHook();
  const getAppText = t("home.footer.gettheapp");
  const paymentOptions = t("home.footer.paymentoptions");
  const isApp = useIsApp();

  if (isApp) return <></>;

  return (
    <>
      {!config.IS_CHINA_APP ? (
        <div className={formatClassNames(classes.outerContainer, isDesktop && classes.desktop)}>
          <div className={classes.container}>
            <div data-testid="footer-get-amber-app" className={classes.headerText}>
              {getAppText}
            </div>
            <div className={classes.imageWrapper}>
              {!config.IS_CHINA_APP ? (
                <CustomLink
                  href={APP_DOWNLOAD_PLAY_STORE_LINK}
                  target="_blank"
                  rel="noreferrer"
                  isExternal
                  dataTestId="footer-get-app-link-Playstore"
                >
                  <LazyImage
                    dataTestId="Play-store-Image"
                    src={getImagePath(`${config.IMAGE_STATIC_URL}/images/playstore-android.svg`)}
                    alt="play store"
                    className={classes.img}
                    height="20px"
                    width="20px"
                  />
                </CustomLink>
              ) : (
                <></>
              )}
              <CustomLink
                href={APP_DOWNLOAD_APP_STORE_LINK}
                target="_blank"
                rel="noreferrer"
                isExternal
                dataTestId="footer-get-app-link-Appstore"
              >
                <LazyImage
                  dataTestId="App-store-Image"
                  src={getImagePath(`${config.IMAGE_STATIC_URL}/images/app-store.svg`)}
                  alt="app store"
                  className={classes.img}
                  height="20px"
                  width="20px"
                />
              </CustomLink>
            </div>
          </div>

          <div className={classes.divider} />

          <div className={classes.container}>
            <div data-testid="footer-payment-options" className={classes.headerText}>
              {paymentOptions}
            </div>
            <div>
              <LazyImage
                dataTestId="footer-payment-options-image"
                src={getImagePath(`${config.IMAGE_STATIC_URL}/images/footer_payment_partners.svg`)}
                alt="payment-partners"
                className={classes.imgPartners}
                height="28px"
                width="158px"
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/*
            China mweb + tablet, Figma 6483:19651. One card whose content block is centred
            at every width. At 650 and below the 375/480/650 frames apply: the store marks
            become the full store badges stacked vertically and the payment marks wrap 2x2,
            so the card grows. Above 650 the 900/1200 frames keep the single-row layout.
          */}
          <div className={classes.chinaCard}>
            <div className={classes.chinaBlock}>
              <div data-testid="footer-get-amber-app" className={classes.chinaHeading}>
                {getAppText}
              </div>
              {/* Design order: Android mark first, App Store second, as on desktop. */}
              <div className={classes.chinaStoreIcons}>
                <CustomLink
                  href={CHINA_ANDROID_DOWNLOAD_LINK}
                  target="_blank"
                  rel="noreferrer"
                  isExternal
                  dataTestId="footer-get-app-link-Android"
                >
                  <span
                    className={classes.chinaStoreIcon}
                    data-testid="Android-store-Image"
                    aria-label="android store"
                    role="img"
                  >
                    <AndroidStoreIcon height="22" width="22" />
                  </span>
                </CustomLink>
                <CustomLink
                  href={APP_DOWNLOAD_APP_STORE_LINK}
                  target="_blank"
                  rel="noreferrer"
                  isExternal
                  dataTestId="footer-get-app-link-Appstore"
                >
                  <span className={classes.chinaStoreIcon}>
                    <LazyImage
                      dataTestId="App-store-Image"
                      src={getImagePath(`${config.IMAGE_STATIC_URL}/images/app-store.svg`)}
                      alt="app store"
                      className={classes.chinaAppStoreImg}
                      width="23"
                      height="23"
                    />
                  </span>
                </CustomLink>
              </div>
              <div className={classes.chinaStoreBadges}>
                <CustomLink
                  href={CHINA_ANDROID_DOWNLOAD_LINK}
                  target="_blank"
                  rel="noreferrer"
                  isExternal
                  dataTestId="footer-get-app-badge-Android"
                  className={classes.chinaStoreBadge}
                >
                  <AndroidStoreBadgeChina />
                </CustomLink>
                <CustomLink
                  href={APP_DOWNLOAD_APP_STORE_LINK}
                  target="_blank"
                  rel="noreferrer"
                  isExternal
                  dataTestId="footer-get-app-badge-Appstore"
                  className={classes.chinaStoreBadge}
                >
                  <AppStoreBadgeChina />
                </CustomLink>
              </div>
            </div>

            <span className={classes.chinaRule} />

            <div className={classes.chinaBlock}>
              <div data-testid="footer-payment-options" className={classes.chinaHeading}>
                {paymentOptions}
              </div>
              <div className={classes.chinaPaymentLogosRow}>
                <ChinaPaymentLogos layout="grid" />
              </div>
            </div>
          </div>

          <FooterQRRowChina />

          {/* Mainland licensing. Not drawn in the Figma frame but legally required on .cn. */}
          <div className={classes.ICPContainer}>
            <a className={classes.ICPLink} href={ICP_URL} target="_blank" rel="noopener noreferrer">
              {ICP_LICENCE_NUMBER}
            </a>
          </div>
        </>
      )}
    </>
  );
};

export default wrapperHOC(AppDownload, {
  componentName: "AppDownload-FooterV2-Global",
  showForChina: true,
});
